import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./css/NewDocumentaryTitle.module.css";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Heart,
  Plus,
  Check,
  RotateCcw,
  RotateCw,
  ChevronDown,
  ArrowLeft,
  Share2,
} from "lucide-react";
import Footer from "../../common/UI/Footer";
// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";
// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
// Import required modules
import { Navigation, Mousewheel, FreeMode } from "swiper/modules";
import { useSimilarVideos, useVideoBySlug } from "../../hooks/useVideosHooks";
import Loading from "../../common/HousGlass Loader/Loading";
import { useChaptersBySeriesId } from "../../hooks/useChapters";
import {
  useCheckContentInPlaylist,
  useCreatePlaylist,
} from "../../hooks/usePlayListHooks";
import {
  useLikeActions,
  useLikesCount,
  useLikeStatus,
} from "../../hooks/useLikesHooks";
import useAuthStore from "../../store/authStore";
import LoginModal from "../../common/Authenticated/LoginModal";
import DocumentaryCard from "./DocumentaryCard";
import { Helmet } from 'react-helmet'; // ✅ Import Helmet


const NewDocumentaryTitle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const videoContainerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Video player states
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);

  // Other states
  const [currentVideo, setCurrentVideo] = useState(null);
  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(-1); // -1 means main video
  const [isAddingToLibrary, setIsAddingToLibrary] = useState(false);
  const [isInLibraryState, setIsInLibraryState] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginAction, setLoginAction] = useState("");
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  const [isIOS, setIsIOS] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartTime, setDragStartTime] = useState(0);

  // Get documentary data
  const { video, isLoading, error } = useVideoBySlug(id);
  const { chapters, isLoading: seriesLoading } = useChaptersBySeriesId(
    video?.series_id
  );
  const { videos } = useSimilarVideos(video?.id);

  // Playlist and like hooks
  const {
    data: playlistCheck,
    isLoading: checkingPlaylist,
    refetch: refetchPlaylistCheck,
  } = useCheckContentInPlaylist(video?.id ? "video" : null, video?.id);

  const { mutate: createPlaylist, isLoading: creatingPlaylist } =
    useCreatePlaylist();

  const { data: likeStatusData } = useLikeStatus("video", video?.id);
  const { data: likesCountData } = useLikesCount("video", video?.id);
  const { likeItem, unlikeItem } = useLikeActions();

  // Set initial video when documentary loads
  useEffect(() => {
    if (video) {
      setCurrentVideo(video);
      setCurrentEpisodeIndex(-1);
    }
  }, [video]);
  // Add this useEffect to detect iOS
  useEffect(() => {
    const detectIOS = () => {
      return (
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
      );
    };
    setIsIOS(detectIOS());
  }, []);

  // Update library state
  useEffect(() => {
    if (playlistCheck) {
      setIsInLibraryState(playlistCheck.inPlaylist);
    }
  }, [playlistCheck]);
  // Add autoplay effect after video loads
  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement && currentVideo) {
      // Set iOS specific attributes
      if (isIOS) {
        videoElement.setAttribute("playsinline", "");
        videoElement.setAttribute("webkit-playsinline", "");
        videoElement.setAttribute("controls", "false");
        // Allow fullscreen on iOS
        videoElement.setAttribute("allowfullscreen", "");
        videoElement.setAttribute("webkitallowfullscreen", "");
      } else {
        videoElement.setAttribute("autoplay", "");
        videoElement.setAttribute("playsinline", "");
        videoElement.setAttribute("webkit-playsinline", "");
      }

      const handleCanPlay = () => {
        if (!isIOS) {
          videoElement.play().catch((error) => {
            console.log("Autoplay failed:", error);
          });
        }
      };

      videoElement.addEventListener("canplay", handleCanPlay);

      return () => {
        videoElement.removeEventListener("canplay", handleCanPlay);
      };
    }
  }, [currentVideo, isIOS]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (isIOS) {
        // For iOS, we manually track fullscreen state
        return;
      }
      setIsFullscreen(!!document.fullscreenElement);
    };

    const handleWebkitFullscreenChange = () => {
      if (isIOS) {
        // For iOS video fullscreen
        const videoElement = videoRef.current;
        if (videoElement) {
          setIsFullscreen(videoElement.webkitDisplayingFullscreen || false);
        }
      } else {
        setIsFullscreen(!!document.webkitFullscreenElement);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener(
      "webkitfullscreenchange",
      handleWebkitFullscreenChange
    );
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    // iOS specific event listeners
    if (isIOS && videoRef.current) {
      const videoElement = videoRef.current;

      const handleWebkitBeginFullscreen = () => {
        setIsFullscreen(true);
      };

      const handleWebkitEndFullscreen = () => {
        setIsFullscreen(false);
      };

      videoElement.addEventListener(
        "webkitbeginfullscreen",
        handleWebkitBeginFullscreen
      );
      videoElement.addEventListener(
        "webkitendfullscreen",
        handleWebkitEndFullscreen
      );

      return () => {
        videoElement.removeEventListener(
          "webkitbeginfullscreen",
          handleWebkitBeginFullscreen
        );
        videoElement.removeEventListener(
          "webkitendfullscreen",
          handleWebkitEndFullscreen
        );
        document.removeEventListener(
          "fullscreenchange",
          handleFullscreenChange
        );
        document.removeEventListener(
          "webkitfullscreenchange",
          handleWebkitFullscreenChange
        );
        document.removeEventListener(
          "mozfullscreenchange",
          handleFullscreenChange
        );
        document.removeEventListener(
          "MSFullscreenChange",
          handleFullscreenChange
        );
      };
    }

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleWebkitFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, [isIOS]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!videoRef.current) return;

      // Prevent default behavior if video is focused or fullscreen
      const isVideoFocused =
        document.activeElement === videoRef.current ||
        document.activeElement === videoContainerRef.current ||
        isFullscreen;

      if (!isVideoFocused && !isFullscreen) return;

      switch (e.code) {
        case "Space":
          e.preventDefault();
          togglePlayPause();
          break;
        case "ArrowLeft":
          e.preventDefault();
          skipBackward();
          break;
        case "ArrowRight":
          e.preventDefault();
          skipForward();
          break;
        case "ArrowUp":
          e.preventDefault();
          changeVolume(0.1);
          break;
        case "ArrowDown":
          e.preventDefault();
          changeVolume(-0.1);
          break;
        case "KeyM":
          e.preventDefault();
          toggleMute();
          break;
        case "KeyF":
          e.preventDefault();
          toggleFullscreen();
          break;
        case "Escape":
          if (isFullscreen) {
            toggleFullscreen();
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [isFullscreen, isPlaying, volume, isMuted]);

  // Video event handlers
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleLoadedMetadata = () => {
      setDuration(videoElement.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(videoElement.currentTime);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setIsBuffering(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleWaiting = () => {
      setIsBuffering(true);
    };

    const handleCanPlay = () => {
      setIsBuffering(false);
    };

    const handleEnded = () => {
      handleNextVideo();
    };

    videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    videoElement.addEventListener("timeupdate", handleTimeUpdate);
    videoElement.addEventListener("play", handlePlay);
    videoElement.addEventListener("pause", handlePause);
    videoElement.addEventListener("waiting", handleWaiting);
    videoElement.addEventListener("canplay", handleCanPlay);
    videoElement.addEventListener("ended", handleEnded);

    return () => {
      videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      videoElement.removeEventListener("timeupdate", handleTimeUpdate);
      videoElement.removeEventListener("play", handlePlay);
      videoElement.removeEventListener("pause", handlePause);
      videoElement.removeEventListener("waiting", handleWaiting);
      videoElement.removeEventListener("canplay", handleCanPlay);
      videoElement.removeEventListener("ended", handleEnded);
    };
  }, [currentVideo]);

  // Auto-hide controls with improved timeout management
  useEffect(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    if (isPlaying && showControls) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2000);
    }

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isPlaying, showControls]);

  // Video control functions
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const skipForward = () => {
    if (videoRef.current) {
      const newTime = Math.min(videoRef.current.currentTime + 10, duration);
      videoRef.current.currentTime = newTime;
      showControlsTemporarily();
    }
  };

  const skipBackward = () => {
    if (videoRef.current) {
      const newTime = Math.max(videoRef.current.currentTime - 10, 0);
      videoRef.current.currentTime = newTime;
      showControlsTemporarily();
    }
  };

  const changeVolume = (delta) => {
    if (videoRef.current) {
      const newVolume = Math.max(0, Math.min(1, volume + delta));
      setVolume(newVolume);
      videoRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
      showControlsTemporarily();
    }
  };

  const showControlsTemporarily = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  const handleProgressClick = (e) => {
    if (videoRef.current && progressRef.current && !isDragging) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const newTime = (clickX / width) * duration;
      videoRef.current.currentTime = newTime;
    }
  };
  const handleProgressMouseDown = (e) => {
    if (!videoRef.current || !progressRef.current) return;

    setIsDragging(true);
    setDragStartTime(currentTime);

    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const newTime = (clickX / width) * duration;

    // Update video time immediately
    videoRef.current.currentTime = newTime;

    // Show controls while dragging
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    // Prevent default to avoid text selection
    e.preventDefault();
  };
  const handleProgressMouseMove = (e) => {
    if (!isDragging || !videoRef.current || !progressRef.current) return;

    const rect = progressRef.current.getBoundingClientRect();
    const moveX = e.clientX - rect.left;
    const width = rect.width;
    const newTime = Math.max(0, Math.min(duration, (moveX / width) * duration));

    videoRef.current.currentTime = newTime;
    e.preventDefault();
  };

  const handleProgressMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      // Resume auto-hide controls behavior
      if (isPlaying) {
        controlsTimeoutRef.current = setTimeout(() => {
          setShowControls(false);
        }, 3000);
      }
    }
  };

  // Touch event handlers for mobile
  const handleProgressTouchStart = (e) => {
    if (!videoRef.current || !progressRef.current) return;

    setIsDragging(true);
    setDragStartTime(currentTime);

    const touch = e.touches[0];
    const rect = progressRef.current.getBoundingClientRect();
    const touchX = touch.clientX - rect.left;
    const width = rect.width;
    const newTime = (touchX / width) * duration;

    videoRef.current.currentTime = newTime;
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    e.preventDefault();
  };

  const handleProgressTouchMove = (e) => {
    if (!isDragging || !videoRef.current || !progressRef.current) return;

    const touch = e.touches[0];
    const rect = progressRef.current.getBoundingClientRect();
    const touchX = touch.clientX - rect.left;
    const width = rect.width;
    const newTime = Math.max(
      0,
      Math.min(duration, (touchX / width) * duration)
    );

    videoRef.current.currentTime = newTime;
    e.preventDefault();
  };

  const handleProgressTouchEnd = () => {
    if (isDragging) {
      setIsDragging(false);
      if (isPlaying) {
        controlsTimeoutRef.current = setTimeout(() => {
          setShowControls(false);
        }, 3000);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);

      // If unmuting and volume is 0, set to a reasonable level
      if (!newMutedState && volume === 0) {
        const newVolume = 0.5;
        setVolume(newVolume);
        videoRef.current.volume = newVolume;
      }
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
    }
  };
  const formatDocumentaryData = (apiDoc) => ({
    id: apiDoc.id,
    title: apiDoc.title,
    slug: apiDoc.slug,
    description: apiDoc.description,
    computer_image_url: apiDoc.computer_image_url || "/fallback-image.jpg",
    mobile_image_url: apiDoc.mobile_image_url,
    author_name: apiDoc.author_name,
    duration: formatDuration(apiDoc.duration),
    category: apiDoc.category,
    video_url: apiDoc.video_url,
  });

  // Add this helper function for duration formatting:
  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const toggleFullscreen = async () => {
    try {
      if (isIOS) {
        // For iOS, use video element's webkitEnterFullscreen
        const videoElement = videoRef.current;
        if (!document.fullscreenElement && videoElement) {
          if (videoElement.webkitEnterFullscreen) {
            videoElement.webkitEnterFullscreen();
            setIsFullscreen(true);
          } else if (videoElement.webkitRequestFullscreen) {
            await videoElement.webkitRequestFullscreen();
          }
        } else {
          if (videoElement && videoElement.webkitExitFullscreen) {
            videoElement.webkitExitFullscreen();
            setIsFullscreen(false);
          } else if (document.webkitExitFullscreen) {
            await document.webkitExitFullscreen();
          }
        }
      } else {
        // For other devices, use container fullscreen
        if (!document.fullscreenElement) {
          const element = videoContainerRef.current;
          if (element.requestFullscreen) {
            await element.requestFullscreen();
          } else if (element.webkitRequestFullscreen) {
            await element.webkitRequestFullscreen();
          } else if (element.mozRequestFullScreen) {
            await element.mozRequestFullScreen();
          } else if (element.msRequestFullscreen) {
            await element.msRequestFullscreen();
          }
        } else {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            await document.webkitExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            await document.mozCancelFullScreen();
          } else if (document.msExitFullscreen) {
            await document.msExitFullscreen();
          }
        }
      }
    } catch (error) {
      console.error("Fullscreen toggle failed:", error);
    }
  };

  const handleNextVideo = () => {
    if (chapters && chapters.length > 0) {
      const nextIndex = currentEpisodeIndex + 1;
      if (nextIndex < chapters.length) {
        handleEpisodeClick(chapters[nextIndex], nextIndex);
      } else if (currentEpisodeIndex === -1) {
        // If we're on main video, go to first episode
        handleEpisodeClick(chapters[0], 0);
      }
    }
  };

  const handlePreviousVideo = () => {
    if (chapters && chapters.length > 0) {
      if (currentEpisodeIndex > 0) {
        handleEpisodeClick(
          chapters[currentEpisodeIndex - 1],
          currentEpisodeIndex - 1
        );
      } else if (currentEpisodeIndex === 0) {
        // Go back to main video
        setCurrentVideo(video);
        setCurrentEpisodeIndex(-1);
      }
    }
  };

  const handleEpisodeClick = (chapter, index) => {
    const episodeVideo = {
      id: chapter.video_id,
      title: chapter.video_title,
      video_url: chapter.video_url,
      description: chapter.video_description,
      duration: chapter.video_duration,
    };
    setCurrentVideo(episodeVideo);
    setCurrentEpisodeIndex(index);
  };

  const handleMainVideoClick = () => {
    setCurrentVideo(video);
    setCurrentEpisodeIndex(-1);
  };

  // Mouse handlers for video container
  const handleMouseMove = () => {
    if (!isFullscreen) {
      showControlsTemporarily();
    } else {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  const handleMouseLeave = () => {
    // Only hide controls when not in fullscreen and playing
    if (!isFullscreen && isPlaying) {
      setShowControls(false);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    }
  };

  // Other handlers
  const handleLogin = () => {
    setIsLoginModalOpen(false);
    navigate("/signin");
  };

  const handleAddToLibrary = () => {
    if (!isAuthenticated) {
      setLoginAction("library");
      setIsLoginModalOpen(true);
      return;
    }

    if (!video?.id || isAddingToLibrary) return;

    setIsAddingToLibrary(true);
    createPlaylist(
      {
        item_type: "video",
        content_id: video.id,
        title: `${video.title} - Library`,
        description: `Library entry for ${video.title}`,
      },
      {
        onSuccess: () => {
          setIsInLibraryState(true);
          refetchPlaylistCheck();
        },
        onSettled: () => {
          setIsAddingToLibrary(false);
        },
      }
    );
  };

  const handleLikeClick = () => {
    if (!isAuthenticated) {
      setLoginAction("like");
      setIsLoginModalOpen(true);
      return;
    }

    if (!video?.id) return;

    if (likeStatusData?.liked) {
      unlikeItem({
        item_type: "video",
        item_id: video.id,
      });
    } else {
      likeItem({
        item_type: "video",
        item_id: video.id,
      });
    }
  };

  const getLoginModalMessage = () => {
    switch (loginAction) {
      case "library":
        return "You have to login first to add this video to your library";
      case "like":
        return "You have to login first to like this video";
      default:
        return "You have to login first for this action";
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };
  // Add global mouse events for dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isDragging) {
        handleProgressMouseMove(e);
      }
    };

    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleProgressMouseUp();
      }
    };

    const handleGlobalTouchMove = (e) => {
      if (isDragging) {
        handleProgressTouchMove(e);
      }
    };

    const handleGlobalTouchEnd = () => {
      if (isDragging) {
        handleProgressTouchEnd();
      }
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
      document.addEventListener("touchmove", handleGlobalTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleGlobalTouchEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("touchmove", handleGlobalTouchMove);
      document.removeEventListener("touchend", handleGlobalTouchEnd);
    };
  }, [isDragging, duration, currentTime]);

  // Add share functionality
  const handleShare = async () => {
    const shareData = {
      title: currentVideo?.title,
      text: `Check out this documentary: ${currentVideo?.title}`,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log("Error sharing:", error);
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setShareMessage("Link copied to clipboard!");
      setIsShareModalOpen(true);
      setTimeout(() => {
        setIsShareModalOpen(false);
        setShareMessage("");
      }, 2000);
    });
  };

  // Add back button handler
  const handleBackClick = () => {
    navigate("/video");
  };

  if (isLoading || !video) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  if (error) {
    return <div>Error loading documentary: {error.message}</div>;
  }

  const likesCount = likesCountData?.count || 0;
  const isLiked = likeStatusData?.liked || false;

  return (
    <>
    <Helmet>
      <title>{currentVideo?.page_title ? `${currentVideo.page_title} - SunoKahaniyan` : currentVideo?.title ? `${currentVideo.title} - SunoKahaniyan` : "SunoKahaniyan"}</title>
      <meta name="description" content={currentVideo?.meta_description || currentVideo?.description || "Watch documentaries on SunoKahaniyan"} />
      {currentVideo?.meta_keywords && <meta name="keywords" content={currentVideo.meta_keywords} />}
      <meta property="og:title" content={currentVideo?.page_title || currentVideo?.title || "SunoKahaniyan"} />
      <meta property="og:description" content={currentVideo?.meta_description || currentVideo?.description || "Watch documentaries on SunoKahaniyan"} />
      <meta property="og:image" content={currentVideo?.computer_image_url || currentVideo?.mobile_image_url || "/fallback-image.jpg"} />
      <meta property="og:type" content="video.movie" />
      <meta property="og:url" content={window.location.href} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={currentVideo?.page_title || currentVideo?.title || "SunoKahaniyan"} />
      <meta name="twitter:description" content={currentVideo?.meta_description || currentVideo?.description || "Watch documentaries on SunoKahaniyan"} />
      <meta name="twitter:image" content={currentVideo?.computer_image_url || currentVideo?.mobile_image_url || "/fallback-image.jpg"} />
      <link rel="canonical" href={window.location.href} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoObject",
          "name": currentVideo?.page_title || currentVideo?.title,
          "description": currentVideo?.meta_description || currentVideo?.description,
          "thumbnailUrl": currentVideo?.computer_image_url || currentVideo?.mobile_image_url,
          "uploadDate": currentVideo?.release_date,
          "duration": currentVideo?.duration ? `PT${Math.floor(currentVideo.duration / 60)}M${currentVideo.duration % 60}S` : undefined,
          "contentUrl": currentVideo?.video_url,
          "embedUrl": currentVideo?.video_url,
          "author": {
            "@type": "Person",
            "name": currentVideo?.author_name || "Unknown"
          }
        })}
      </script>
    </Helmet>
      <div
        className={`${styles.container} ${
          isFullscreen ? styles.fullscreenContainer : ""
        }`}
      >
        {/* Video Player Section */}
        <div className={styles.videoSection}>
          <div>
            <button
              className={styles.backButton}
              onClick={handleBackClick}
              title="Go Back"
            >
              <ArrowLeft size={30} />
            </button>
            <div
              ref={videoContainerRef}
              className={`${styles.videoContainer} ${
                isFullscreen ? styles.fullscreenVideo : ""
              }`}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              tabIndex={0}
            >
              <video
                ref={videoRef}
                className={styles.videoPlayer}
                src={currentVideo?.video_url}
                poster={
                  currentVideo?.computer_image_url ||
                  currentVideo?.mobile_image_url
                }
                onClick={togglePlayPause}
                playsInline
                webkitPlaysInline
                autoPlay
                muted={false}
              />

              {/* Loading Spinner */}
              {isBuffering && (
                <div className={styles.loadingSpinner}>
                  <div className={styles.spinner}></div>
                </div>
              )}

              {/* Video Controls */}
              <div
                className={`${styles.videoControls} ${
                  showControls ? styles.showControls : ""
                }`}
              >
                {/* Progress Bar */}
                <div
                  className={styles.progressContainer}
                  ref={progressRef}
                  onClick={handleProgressClick}
                  onMouseDown={handleProgressMouseDown}
                  onTouchStart={handleProgressTouchStart}
                  style={{ cursor: isDragging ? "grabbing" : "pointer" }}
                >
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFilled}
                      style={{ width: `${(currentTime / duration) * 100}%` }}
                    ></div>
                    {/* Progress Handle/Thumb */}
                    <div
                      className={styles.progressHandle}
                      style={{
                        left: `${(currentTime / duration) * 100}%`,
                        opacity: showControls || isDragging ? 1 : 0,
                      }}
                    />
                  </div>
                  {/* Time Preview on Hover */}
                  {isDragging && (
                    <div
                      className={styles.timePreview}
                      style={{ left: `${(currentTime / duration) * 100}%` }}
                    >
                      {formatTime(currentTime)}
                    </div>
                  )}
                </div>

                {/* Control Buttons */}
                <div className={styles.controlsRow}>
                  <div className={styles.leftControls}>
                    <button
                      className={styles.controlButton}
                      onClick={skipBackward}
                      title="Backward 10s"
                    >
                      <RotateCcw size={22} />
                      <span className={styles.skipText}>10</span>
                    </button>

                    <button
                      className={styles.playButton}
                      onClick={togglePlayPause}
                      title={isPlaying ? "Pause" : "Play"}
                    >
                      {isPlaying ? <Pause size={24} /> : <Play size={24} />}
                    </button>

                    <button
                      className={styles.controlButton}
                      onClick={skipForward}
                      title="Forward 10s"
                    >
                      <RotateCw size={22} />
                      <span className={styles.skipText}>10</span>
                    </button>

                    <div className={styles.volumeContainer}>
                      <button
                        className={styles.controlButton}
                        onClick={toggleMute}
                        title={isMuted ? "Unmute" : "Mute"}
                      >
                        {isMuted || volume === 0 ? (
                          <VolumeX size={20} />
                        ) : (
                          <Volume2 size={20} />
                        )}
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={isMuted ? 0 : volume}
                        onChange={handleVolumeChange}
                        className={styles.volumeSlider}
                        title="Volume"
                      />
                    </div>

                    <div className={styles.timeDisplay}>
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </div>
                  </div>

                  <div className={styles.rightControls}>
                    <button
                      className={styles.controlButton}
                      onClick={toggleFullscreen}
                      title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                    >
                      {isFullscreen ? (
                        <Minimize size={20} />
                      ) : (
                        <Maximize size={20} />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Keyboard Shortcuts Hint */}
              {isFullscreen && (
                <div className={styles.keyboardHint}>
                  <div className={styles.hintText}>
                    Space: Play/Pause • ←→: Skip 10s • ↑↓: Volume • M: Mute • F:
                    Fullscreen • Esc: Exit
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Video Info */}
          {!isFullscreen && (
            <div className={styles.videoInfo}>
              {/* Back Button */}

              <h1 className={styles.videoTitle}>{currentVideo?.title}</h1>

              {/* Action Buttons Row */}
              <div className={styles.videoMeta}>
                <div className={styles.actionButtons}>
                  <button
                    className={`${styles.actionButton} ${styles.likeButton} ${
                      isLiked ? styles.liked : ""
                    }`}
                    onClick={handleLikeClick}
                  >
                    <Heart
                      size={18}
                      fill={isLiked ? "#e50914" : "none"}
                      stroke={isLiked ? "#e50914" : "currentColor"}
                    />
                    <span>{likesCount}</span>
                  </button>

                  <button
                    className={`${styles.actionButton} ${
                      styles.libraryButton
                    } ${isInLibraryState ? styles.added : ""}`}
                    onClick={handleAddToLibrary}
                    disabled={
                      isInLibraryState || isAddingToLibrary || checkingPlaylist
                    }
                  >
                    {isAddingToLibrary ? (
                      <>
                        <div className={styles.loadingSpinner}></div>
                        Adding...
                      </>
                    ) : checkingPlaylist ? (
                      <>
                        <div className={styles.loadingSpinner}></div>
                        Checking...
                      </>
                    ) : isInLibraryState ? (
                      <>
                        <Check size={18} />
                        Added to Library
                      </>
                    ) : (
                      <>
                        <Plus size={18} />
                        Add to Library
                      </>
                    )}
                  </button>

                  <button
                    className={`${styles.actionButton} ${styles.shareButton}`}
                    onClick={handleShare}
                  >
                    <Share2 size={18} />
                    <span>Share</span>
                  </button>
                </div>
              </div>

              {/* See More Button */}
              <button
                className={styles.seeMoreButton}
                onClick={() => setShowMoreInfo(!showMoreInfo)}
              >
                <span>See {showMoreInfo ? "Less" : "More"}</span>
                <ChevronDown
                  size={20}
                  className={`${styles.chevronIcon} ${
                    showMoreInfo ? styles.rotated : ""
                  }`}
                />
              </button>

              {/* Expandable Content */}
              <div
                className={`${styles.expandableContent} ${
                  showMoreInfo ? styles.expanded : ""
                }`}
              >
                {/* Detailed Info */}
                <div className={styles.detailedInfo}>
                  <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Language:</span>
                      <span className={styles.infoValue}>
                        {video.language || "English"}
                      </span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Category:</span>
                      <span className={styles.infoValue}>{video.category}</span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Author:</span>
                      <span className={styles.infoValue}>
                        {video.author_name || "Unknown Author"}
                      </span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Duration:</span>
                      <span className={styles.infoValue}>
                        {formatTime(currentVideo?.duration || 0)}
                      </span>
                    </div>
                    {video.release_date && (
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Release Date:</span>
                        <span className={styles.infoValue}>
                          {new Date(video.release_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {video.rating && (
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Rating:</span>
                        <span className={styles.infoValue}>
                          {video.rating}/10
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                {currentVideo?.description && (
                  <div className={styles.videoDescription}>
                    <h4 className={styles.descriptionTitle}>Description</h4>
                    <p>{currentVideo.description}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Episodes Section */}
        {!isFullscreen &&
          video.have_series &&
          video.series_id &&
          !seriesLoading &&
          chapters?.length > 0 && (
            <div className={styles.episodesSection}>
              <h3 className={styles.sectionTitle}>Episodes</h3>
              <div className={styles.episodesList}>
                {/* Main Video */}
                <div
                  className={`${styles.episodeItem} ${
                    currentEpisodeIndex === -1 ? styles.currentEpisode : ""
                  }`}
                  onClick={handleMainVideoClick}
                >
                  <img
                    src={
                      video.mobile_image_url ||
                      "/images/movie-poster-design.avif"
                    }
                    alt={video.title}
                    className={styles.episodeThumbnail}
                  />
                  <div className={styles.episodeInfo}>
                    <h4 className={styles.episodeTitle}>
                      {video.title} (Main)
                    </h4>
                    <p className={styles.episodeDescription}>
                      {video.description}
                    </p>
                    <span className={styles.episodeDuration}>
                      {formatTime(video.duration)}
                    </span>
                  </div>
                </div>

                {/* Episodes */}
                {chapters.map((chapter, index) => (
                  <div
                    key={chapter.id}
                    className={`${styles.episodeItem} ${
                      currentEpisodeIndex === index ? styles.currentEpisode : ""
                    }`}
                    onClick={() => handleEpisodeClick(chapter, index)}
                  >
                    <img
                      src={
                        chapter.video_mobile_image ||
                        "/images/movie-poster-design.avif"
                      }
                      alt={chapter.video_title}
                      className={styles.episodeThumbnail}
                    />
                    <div className={styles.episodeInfo}>
                      <h4 className={styles.episodeTitle}>
                        {chapter.video_title}
                      </h4>
                      <p className={styles.episodeDescription}>
                        {chapter.video_description}
                      </p>
                      <span className={styles.episodeDuration}>
                        {formatTime(chapter.video_duration)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Similar Videos */}
        {!isFullscreen && (
          <div className={styles.similarSection}>
            <h3 className={styles.sectionTitle}>Similar Videos</h3>
            <Swiper
              freeMode={{
                enabled: true,
                momentum: true,
                momentumRatio: 1,
                momentumVelocityRatio: 1,
                minimumVelocity: 0.02,
              }}
              modules={[Navigation, Mousewheel, FreeMode]}
              mousewheel={{
                forceToAxis: true,
                sensitivity: 0.5,
                releaseOnEdges: true,
              }}
              speed={400}
              resistance={true}
              resistanceRatio={0.85}
              breakpoints={{
                320: {
                  slidesPerView: 1.5,
                  spaceBetween: 10,
                },
                350: {
                  slidesPerView: 2,
                  spaceBetween: 10,
                },
                640: {
                  slidesPerView: 2,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 3,
                  spaceBetween: 30,
                },
                1024: {
                  slidesPerView: 4,
                  spaceBetween: 30,
                },
              }}
              className={styles.similarSwiper}
            >
              {videos.map((similarVideo) => (
                <SwiperSlide key={similarVideo.id}>
                  <DocumentaryCard
                    documentary={formatDocumentaryData(similarVideo)}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
      </div>

      {!isFullscreen && <Footer />}

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={handleLogin}
        message={getLoginModalMessage()}
      />
      {/* Share Modal */}
      {isShareModalOpen && (
        <div className={styles.shareModal}>
          <div className={styles.shareModalContent}>
            <p>{shareMessage}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default NewDocumentaryTitle;
